import { withLegacyCompat } from '@adornis/buchungsheld-components/legacy-editor-compat.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { confirmDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { HtmlEditor, type HtmlEditorProps } from '@adornis/html-editor/client/HtmlEditor.js';
import { richTextEditorConfig } from '@adornis/prosemirror-editor/presets/rich-text-editor.js';
import { html, nothing } from 'lit';
import * as m from '../../../localization/out/messages.js';
import { FreeFormMailRecipient, previewFreeFormMail, sendFreeFormMailToUsers } from '../../api/admin-mail.js';
import { FREE_FORM_MAIL_PLACEHOLDERS } from '../../shared/free-form-mail.js';

type EditorView = Parameters<NonNullable<HtmlEditorProps['onViewReady']>>[0];
const recipientName = (recipient: FreeFormMailRecipient) =>
  `${recipient.firstName ?? ''} ${recipient.lastName ?? ''}`.trim() || recipient.email;

const MailComposeDialog = stateful((props: { recipients: FreeFormMailRecipient[]; close: (sent: boolean) => void }) => {
  const request = useMemo(() => [{ id: crypto.randomUUID(), subject: '', bodyHtml: '' }], []);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [hasBody, setHasBody] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | undefined>(undefined);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [editorRef] = useState<{ view: EditorView; busy: boolean }>({ view: null, busy: false }, []);
  const configs = useMemo(() => {
    const design = DesignSystem.currentTheme.getValue();
    return [
      withLegacyCompat(
        richTextEditorConfig({
          colors: [design.colors.tone.error, design.colors.tone.warning, design.colors.tone.success]
            .filter(color => color !== undefined)
            .map(color => color.toString()),
        }),
      ),
    ];
  }, []);
  const onBodyChanged = (value: string | null | undefined) => {
    const next = value ?? '';
    if (request.bodyHtml !== next) request.id = crypto.randomUUID();
    request.bodyHtml = next;
    setBodyHtml(next);
    setHasBody(!!editorRef.view?.state.doc.textContent.trim());
  };
  const firstRecipient = props.recipients[0];
  const count = props.recipients.length;
  const showError = (error: unknown) =>
    void XSnackbar.show(error instanceof Error ? error.message : m.mail_compose_error());

  const insertPlaceholder = (text: string) => {
    if (sending) return;
    const view = editorRef.view;
    if (view) {
      view.dispatch(view.state.tr.insertText(text));
      view.focus();
    }
    void navigator.clipboard?.writeText(text).catch(() => {});
  };

  const updatePreview = async () => {
    if (!firstRecipient || previewing || sending) return;
    setPreviewing(true);
    try {
      setPreviewHtml(await previewFreeFormMail(subject, bodyHtml, firstRecipient._id)());
    } catch (error) {
      showError(error);
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    if (editorRef.busy || !subject.trim() || !hasBody || !count) return;
    editorRef.busy = true;
    try {
      if (
        !(await confirmDialog(m.mail_compose_confirm({ count }), {
          acceptText: m.mail_compose_send({ count }),
          declineText: m.mail_compose_cancel(),
        }))
      )
        return;
      setSending(true);
      const result = await sendFreeFormMailToUsers(
        props.recipients.map(recipient => recipient._id),
        subject,
        bodyHtml,
        request.id,
      )({ sent: 1, failed: 1 });
      request.id = crypto.randomUUID();
      void XSnackbar.show(
        m.mail_compose_sent({ count: result.sent }) +
          (result.failed > 0 ? m.mail_compose_failed({ count: result.failed }) : ''),
      );
      props.close(result.sent > 0);
    } catch (error) {
      showError(error);
    } finally {
      editorRef.busy = false;
      setSending(false);
    }
  };

  return html`
    <div class="flex flex-col gap-4">
      <h3 class="text-lg font-semibold">${m.mail_compose_title()}</h3>
      <div>
        <p class="font-semibold">${m.mail_compose_recipients({ count })}</p>
        <div class="text-sm break-words">
          ${props.recipients
            .slice(0, expanded ? undefined : 5)
            .map(recipient => html` <div>${recipientName(recipient)} &lt;${recipient.email}&gt;</div> `)}
        </div>
        ${
          !expanded && count > 5
            ? html`
                <button class="btn btn-link btn-sm px-0" @click=${() => setExpanded(true)}>
                  ${m.mail_compose_more_recipients({ count: count - 5 })}
                </button>
              `
            : nothing
        }
      </div>
      <label class="form-control">
        <div class="label"><span class="label-text">${m.mail_compose_subject()}</span></div>
        <input
          class="input input-bordered w-full"
          .value=${subject}
          ?disabled=${sending}
          @input=${(event: Event) => {
            const next = (event.currentTarget as HTMLInputElement).value;
            if (request.subject !== next) request.id = crypto.randomUUID();
            request.subject = next;
            setSubject(next);
          }}
        />
      </label>
      <div class="flex flex-wrap items-center gap-2">
        ${FREE_FORM_MAIL_PLACEHOLDERS.map(
          key => html`
            <span
              class="badge badge-outline cursor-pointer"
              title=${m.mail_compose_placeholder_tooltip()}
              @dblclick=${() => insertPlaceholder(`{{${key}}}`)}
              >{{${key}}}</span
            >
          `,
        )}
        <span class="text-sm opacity-70">${m.mail_compose_placeholder_hint()}</span>
      </div>
      <div>
        <p class="label-text mb-2">${m.mail_compose_body()}</p>
        <div class="w-full min-h-48 px-3 py-2 bg-base-200 border border-base-300 rounded-md">
          ${HtmlEditor({
            configs,
            value: bodyHtml,
            disabled: sending,
            placeholder: m.mail_compose_body_placeholder(),
            onChanged: onBodyChanged,
            onPicked: onBodyChanged,
            onViewReady: view => {
              editorRef.view = view;
            },
          })}
        </div>
      </div>
      <div class="flex flex-wrap gap-2 items-center">
        <button
          class="btn btn-outline btn-sm"
          ?disabled=${previewing || sending || !firstRecipient}
          @click=${updatePreview}
        >
          ${previewing ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing}
          ${m.mail_compose_preview_refresh()}
        </button>
        ${
          previewHtml !== undefined
            ? html`
                <button class="btn btn-ghost btn-sm" @click=${() => setPreviewHtml(undefined)}>
                  ${m.mail_compose_preview_hide()}
                </button>
              `
            : nothing
        }
      </div>
      ${
        previewHtml !== undefined && firstRecipient
          ? html`
              <div>
                <p class="text-sm opacity-70 mb-2">
                  ${m.mail_compose_preview_for({ name: recipientName(firstRecipient) })}
                </p>
                <iframe
                  sandbox=""
                  .srcdoc=${previewHtml}
                  title=${m.mail_compose_preview_for({ name: recipientName(firstRecipient) })}
                  class="w-full h-[480px] border border-base-300 rounded-md"
                ></iframe>
              </div>
            `
          : nothing
      }
      <div class="modal-action justify-end gap-2">
        <button class="btn btn-outline" ?disabled=${sending} @click=${() => props.close(false)}>
          ${m.mail_compose_cancel()}
        </button>
        <button class="btn btn-primary" ?disabled=${sending || !subject.trim() || !hasBody || !count} @click=${send}>
          ${sending ? html`<span class="loading loading-spinner loading-sm"></span>${m.mail_compose_sending()}` : m.mail_compose_send({ count })}
        </button>
      </div>
    </div>
  `;
});

export const openMailComposeDialog = (recipients: FreeFormMailRecipient[]) =>
  showDialog<boolean>({
    modalBoxClasses: 'max-w-4xl',
    modal: true,
    closeOnNavigate: false,
    closeOnEscape: false,
    content: resolve => MailComposeDialog({ recipients, close: resolve }),
  });
